/**
 * GoogleCalendarContext
 * Manages Google Calendar OAuth connection, event push/pull, auto-poll
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import type { CalendarEvent } from '../types'
import {
  initTokenClient, requestAccessToken, revokeToken,
  loadToken, clearToken,
  createGoogleEvent, deleteGoogleEvent, fetchGoogleEvents,
} from '../services/googleCalendar'
import { requestNotificationPermission } from '../services/reminderService'

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined

const POLL_INTERVAL_MS = 60_000  // pull from Google every 60s

// ── Context types ───────────────────────────────────────────────
interface GoogleCalendarCtx {
  /** Whether an access token is present and not expired */
  connected: boolean
  /** OAuth popup in progress */
  connecting: boolean
  /** API call in progress */
  syncing: boolean
  lastSynced: Date | null
  /** Error message, if any */
  error: string | null
  /** Google Client ID is configured */
  configured: boolean
  /** Trigger OAuth popup */
  connect: () => void
  /** Revoke token + disconnect */
  disconnect: () => void
  /** Push one ERP event to Google Calendar, returns googleEventId or null */
  pushEvent: (ev: CalendarEvent) => Promise<string | null>
  /** Delete from Google Calendar by googleEventId */
  removeEvent: (googleEventId: string) => Promise<void>
  /** Fetch Google events and return them as CalendarEvent[] */
  pullEvents: () => Promise<CalendarEvent[]>
}

const GoogleCalendarContext = createContext<GoogleCalendarCtx>({
  connected: false, connecting: false, syncing: false,
  lastSynced: null, error: null, configured: false,
  connect: () => {}, disconnect: () => {},
  pushEvent: async () => null,
  removeEvent: async () => {},
  pullEvents: async () => [],
})

export function useGoogleCalendar() { return useContext(GoogleCalendarContext) }

// ── Provider ────────────────────────────────────────────────────
export function GoogleCalendarProvider({
  children,
  onEventsFromGoogle,
}: {
  children: React.ReactNode
  /** Called whenever new events are pulled from Google */
  onEventsFromGoogle?: (events: CalendarEvent[]) => void
}) {
  const [connected,  setConnected]  = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [syncing,    setSyncing]    = useState(false)
  const [lastSynced, setLastSynced] = useState<Date | null>(null)
  const [error,      setError]      = useState<string | null>(null)
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const onEventsRef = useRef(onEventsFromGoogle)
  onEventsRef.current = onEventsFromGoogle

  const configured = !!CLIENT_ID

  // ── Init ──────────────────────────────────────────────────────
  useEffect(() => {
    // Check if we already have a valid stored token
    const existing = loadToken()
    if (existing) setConnected(true)

    // Initialise the token client once GIS script is loaded
    if (!CLIENT_ID) return
    const tryInit = () => {
      if ((window as any).google?.accounts?.oauth2) {
        initTokenClient(
          CLIENT_ID,
          (token) => {
            setConnected(true)
            setConnecting(false)
            setError(null)
            // Request notification permission on connect
            requestNotificationPermission()
          },
          (err) => {
            setError(err)
            setConnecting(false)
          },
        )
      } else {
        // Script not ready yet — retry
        setTimeout(tryInit, 500)
      }
    }
    tryInit()
  }, [])

  // ── Auto-poll when connected ───────────────────────────────────
  const doPull = useCallback(async () => {
    const token = loadToken()
    if (!token) { setConnected(false); return }
    setSyncing(true)
    try {
      const events = await fetchGoogleEvents(token)
      setLastSynced(new Date())
      onEventsRef.current?.(events)
    } catch (e: any) {
      if (e.message?.includes('401')) {
        // Token expired
        clearToken()
        setConnected(false)
      }
    } finally {
      setSyncing(false)
    }
  }, [])

  useEffect(() => {
    if (connected) {
      doPull()
      pollTimer.current = setInterval(doPull, POLL_INTERVAL_MS)
    } else {
      if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null }
    }
    return () => { if (pollTimer.current) clearInterval(pollTimer.current) }
  }, [connected, doPull])

  // ── connect ───────────────────────────────────────────────────
  const connect = useCallback(() => {
    if (!CLIENT_ID) {
      setError('VITE_GOOGLE_CLIENT_ID is not set in your .env file.')
      return
    }
    setConnecting(true)
    setError(null)
    try {
      requestAccessToken()
    } catch (e: any) {
      setError(e.message ?? 'Could not open Google sign-in')
      setConnecting(false)
    }
  }, [])

  // ── disconnect ────────────────────────────────────────────────
  const disconnect = useCallback(async () => {
    const token = loadToken()
    if (token) await revokeToken(token)
    else clearToken()
    setConnected(false)
    setLastSynced(null)
  }, [])

  // ── pushEvent ─────────────────────────────────────────────────
  const pushEvent = useCallback(async (ev: CalendarEvent): Promise<string | null> => {
    const token = loadToken()
    if (!token || !connected) return null
    setSyncing(true)
    try {
      const gId = await createGoogleEvent(token, ev)
      return gId
    } catch (e: any) {
      setError(e.message)
      return null
    } finally {
      setSyncing(false)
    }
  }, [connected])

  // ── removeEvent ───────────────────────────────────────────────
  const removeEvent = useCallback(async (googleEventId: string): Promise<void> => {
    const token = loadToken()
    if (!token || !connected) return
    setSyncing(true)
    try {
      await deleteGoogleEvent(token, googleEventId)
    } catch (e: any) {
      // 410 Gone = already deleted on Google side, not an error
      if (!e.message?.includes('410') && !e.message?.includes('404')) {
        setError(e.message)
      }
    } finally {
      setSyncing(false)
    }
  }, [connected])

  // ── pullEvents ────────────────────────────────────────────────
  const pullEvents = useCallback(async (): Promise<CalendarEvent[]> => {
    const token = loadToken()
    if (!token || !connected) return []
    setSyncing(true)
    try {
      const events = await fetchGoogleEvents(token)
      setLastSynced(new Date())
      return events
    } catch (e: any) {
      setError(e.message)
      return []
    } finally {
      setSyncing(false)
    }
  }, [connected])

  return (
    <GoogleCalendarContext.Provider value={{
      connected, connecting, syncing, lastSynced, error, configured,
      connect, disconnect, pushEvent, removeEvent, pullEvents,
    }}>
      {children}
    </GoogleCalendarContext.Provider>
  )
}
